import React, { useEffect } from 'react';
import { useServiceLayerAuth } from './ServiceLayerAuthProvider';
import { Navigate } from 'react-router-dom';
import { usePermissions } from '@appic/hooks';

type RequiredRole = 'Admin' | 'Manager' | 'Sales' | 'Viewer' | undefined;

const ProtectedRoute: React.FC<{ children: React.ReactNode; required?: RequiredRole }> = ({ children, required }) => {
  const { isConfigured, email, loading, ready, signIn, authError } = useServiceLayerAuth();
  const { canAdmin, canWrite, canAccess } = usePermissions();

  const meetsRole = (requiredRole: RequiredRole): boolean => {
    if (!requiredRole || requiredRole === 'Viewer') return true;
    if (requiredRole === 'Admin') return canAdmin('settings');
    if (requiredRole === 'Manager') return canWrite('settings');
    if (requiredRole === 'Sales') return canAccess('scout');
    return false;
  };

  useEffect(() => {
    if (!isConfigured) return;
    if (!loading && !email) {
      // Lazy kick-off in case popup blockers interfere
      // Prefer explicit click via LoginGate button
    }
  }, [isConfigured, loading, email, signIn]);

  // CRITICAL: Only show LoginGate if we're ready AND have no email
  // This prevents flicker during navigation when auth check is still running
  if (!ready || loading) {
    // Show nothing while checking auth - prevents logout screen flicker
    return null;
  }

  if (!email) {
    // In dev/test mode, render children directly (cookie auth can't work from localhost)
    const env = import.meta.env;
    const TEST_MODE = env.VITE_TEST_MODE === '1' || (window as Window & { __TEST_MODE__?: boolean }).__TEST_MODE__;
    const IS_DEV = (env.DEV || env.MODE === 'development') && env.VITE_FORCE_REAL_AUTH !== 'true';
    if (TEST_MODE || IS_DEV) {
      return <>{children}</>;
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
        <div className="w-full max-w-sm p-6 rounded-lg bg-card border border-border">
          <div className="flex flex-col items-center gap-4">
            <h1 className="text-xl font-semibold">CLANDA</h1>
            <p className="text-sm text-muted-foreground">Bitte mit CLANDA.de E‑Mail-Adresse anmelden</p>
            {authError && (
              <div className="w-full p-3 rounded text-sm text-center bg-danger/10 text-danger border border-danger" role="alert">
                {authError}
              </div>
            )}
            <button
              type="button"
              onClick={() => signIn()}
              disabled={loading}
              className="w-full px-4 py-2 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 disabled:opacity-50"
            >
              Anmelden
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!meetsRole(required)) {
    return <Navigate to="/start" replace />;
  }
  return <>{children}</>;
};

export default ProtectedRoute;
